// src/engine/autoMapper.ts
//
// Board-level automapper v2: analyzes all devices on the board and suggests
// a logical signal flow based on device categories, port roles, and
// studio routing conventions.
//
// Signal flow (audio):
//   Sources (synth, drum_machine, sampler, groovebox, semi_modular)
//     → Mixer inputs (if mixer present) OR Interface inputs (direct)
//   Effects: aux sends from mixer, or inline between source and destination
//   Mixer main outs → Monitor Controller (if present) OR Interface
//   Interface outs → Monitor Controller (if present) → Sub → Monitors
//
// Monitoring chain priority:
//   Interface → Monitor Controller → Subwoofer (thru outs) → Monitors
//   If no monitor controller: Interface → Sub → Monitors
//   If no sub: Interface → Monitors (or Monitor Controller → Monitors)
//
// MIDI flow:
//   Controller → MIDI Interface (if present) → sound sources
//   Controller → sound sources (direct, if no MIDI interface)
//
// Sampler routing:
//   Interface outputs → Sampler inputs (for resampling)
//
// Port exclusivity: each port may only appear in one suggestion.

import { Device, Connection, Port, SignalType, DeviceCategory } from '@/types'

// ── Types ──

export interface SuggestedConnection {
  id: string
  sourceDevice: Device
  targetDevice: Device
  sourcePort: Port
  targetPort: Port
  signalType: SignalType
  reason: string
  priority: number
}

// ── Category classification ──

const AUDIO_SOURCES: DeviceCategory[] = [
  'synth',
  'drum_machine',
  'sampler',
  'groovebox',
  'semi_modular',
  'recorder',
  'microphone',
]

/** Categories that provide USB host connectivity */
const USB_HOSTS: DeviceCategory[] = ['computer', 'usb_hub']

// ── Port role detection via name patterns ──

/** Is this a "main" output? (Main Out, Main L/R, Control Room, Monitor Out) */
function isMainOutput(port: Port): boolean {
  const n = port.name.toLowerCase()
  return (
    n.includes('main') ||
    n.includes('control room') ||
    n.includes('monitor out') ||
    n.includes('master')
  )
}

/** Is this an aux/bus/group send (not a main output)? Exported for future effects routing. */
export function isAuxOutput(port: Port): boolean {
  const n = port.name.toLowerCase()
  return (
    n.includes('aux') ||
    n.includes('bus') ||
    n.includes('group') ||
    n.includes('sub ') || // "Sub 1", "Sub 2" on mixers = subgroup, not subwoofer
    n.includes('2-track')
  )
}

/** Is this a "thru" output (passthrough, e.g., on subs)? */
function isThruOutput(port: Port): boolean {
  const n = port.name.toLowerCase()
  return n.includes('thru')
}

/** Is this a "line out" on an interface (numbered outputs)? */
function isLineOut(port: Port): boolean {
  const n = port.name.toLowerCase()
  return n.includes('line out')
}

/** Is this a "line in" on an interface? */
function isLineIn(port: Port): boolean {
  const n = port.name.toLowerCase()
  return (
    n.includes('line in') || n.includes('mic/line') || n.includes('instrument')
  )
}

/** Get stereo pairs from a list of ports (L/R, 1/2, A/B patterns) */
function getStereoPairs(ports: Port[]): [Port, Port][] {
  const pairs: [Port, Port][] = []
  const used = new Set<string>()

  for (let i = 0; i < ports.length; i++) {
    if (used.has(ports[i].id)) continue
    const n = ports[i].name.toLowerCase()

    // Look for L/R pair
    for (let j = i + 1; j < ports.length; j++) {
      if (used.has(ports[j].id)) continue
      const m = ports[j].name.toLowerCase()

      const isLR =
        (n.includes(' l') &&
          m.includes(' r') &&
          n.replace(' l', '') === m.replace(' r', '')) ||
        (n.includes(' l ') &&
          m.includes(' r ') &&
          n.replace(' l ', ' r ') === m)

      // Numbered pairs: "Out 1" + "Out 2", "In 1" + "In 2"
      const numMatch1 = n.match(/(\d+)$/)
      const numMatch2 = m.match(/(\d+)$/)
      const isNumberedPair =
        numMatch1 &&
        numMatch2 &&
        parseInt(numMatch1[1]) % 2 === 1 &&
        parseInt(numMatch2[1]) === parseInt(numMatch1[1]) + 1 &&
        n.replace(numMatch1[1], '') === m.replace(numMatch2[1], '')

      if (isLR || isNumberedPair) {
        pairs.push([ports[i], ports[j]])
        used.add(ports[i].id)
        used.add(ports[j].id)
        break
      }
    }
  }

  return pairs
}

/** Get the first available stereo pair of audio ports from a list */
function getFirstAvailablePair(
  ports: Port[],
  usedPorts: Set<string>,
  deviceId: string,
  filter?: (p: Port) => boolean,
): [Port, Port] | null {
  const candidates = ports.filter(
    p =>
      p.signalType === 'audio' &&
      !usedPorts.has(`${deviceId}:${p.id}`) &&
      (!filter || filter(p)),
  )
  const pairs = getStereoPairs(candidates)
  return pairs.length > 0 ? pairs[0] : null
}

/** Get the first available single audio port (for single-input monitors like ADAM A8H) */
function getFirstAvailableSingle(
  ports: Port[],
  usedPorts: Set<string>,
  deviceId: string,
  filter?: (p: Port) => boolean,
): Port | null {
  return (
    ports.find(
      p =>
        p.signalType === 'audio' &&
        !usedPorts.has(`${deviceId}:${p.id}`) &&
        (!filter || filter(p)),
    ) || null
  )
}

/**
 * Connect a source device to a monitor, handling both stereo-pair monitors
 * (e.g. Mackie Big Knob with L/R inputs) and single-input monitors (e.g. ADAM A8H).
 * For single-input monitors, only one output port is consumed from the source.
 */
function connectToMonitor(
  srcDev: Device,
  srcPorts: Port[],
  monitor: Device,
  usedOutputs: Set<string>,
  usedInputs: Set<string>,
  suggestions: SuggestedConnection[],
  reason: string,
  priority: number,
  srcFilter?: (p: Port) => boolean,
): boolean {
  // Try stereo pair first
  const monInPair = getFirstAvailablePair(
    monitor.inputs,
    usedInputs,
    monitor.id,
  )
  if (monInPair) {
    const srcPair = getFirstAvailablePair(
      srcPorts,
      usedOutputs,
      srcDev.id,
      srcFilter,
    )
    if (srcPair) {
      connectPair(
        srcDev,
        srcPair,
        monitor,
        monInPair,
        usedOutputs,
        usedInputs,
        suggestions,
        reason,
        priority,
      )
      return true
    }
  }

  // Fall back to single-port connection (e.g. ADAM A8H)
  const monIn = getFirstAvailableSingle(monitor.inputs, usedInputs, monitor.id)
  if (monIn) {
    const srcOut = getFirstAvailableSingle(
      srcPorts,
      usedOutputs,
      srcDev.id,
      srcFilter,
    )
    if (srcOut) {
      suggestions.push(
        makeSuggestion(srcDev, srcOut, monitor, monIn, reason, priority),
      )
      markUsed(usedOutputs, usedInputs, srcDev, srcOut, monitor, monIn)
      return true
    }
  }

  return false
}

// ── Helpers ──

function markUsed(
  usedOutputs: Set<string>,
  usedInputs: Set<string>,
  srcDev: Device,
  srcPort: Port,
  tgtDev: Device,
  tgtPort: Port,
) {
  usedOutputs.add(`${srcDev.id}:${srcPort.id}`)
  usedInputs.add(`${tgtDev.id}:${tgtPort.id}`)

  // Mark exclusive-group siblings as used (parallel connectors)
  if (srcPort.exclusiveGroup) {
    for (const sib of srcDev.outputs) {
      if (
        sib.id !== srcPort.id &&
        sib.exclusiveGroup === srcPort.exclusiveGroup
      ) {
        usedOutputs.add(`${srcDev.id}:${sib.id}`)
      }
    }
  }
  if (tgtPort.exclusiveGroup) {
    for (const sib of tgtDev.inputs) {
      if (
        sib.id !== tgtPort.id &&
        sib.exclusiveGroup === tgtPort.exclusiveGroup
      ) {
        usedInputs.add(`${tgtDev.id}:${sib.id}`)
      }
    }
  }
}

function makeSuggestion(
  srcDev: Device,
  srcPort: Port,
  tgtDev: Device,
  tgtPort: Port,
  reason: string,
  priority: number,
): SuggestedConnection {
  return {
    id: `auto-${srcDev.id}-${tgtDev.id}-${srcPort.id}-${tgtPort.id}`,
    sourceDevice: srcDev,
    targetDevice: tgtDev,
    sourcePort: srcPort,
    targetPort: tgtPort,
    signalType: srcPort.signalType,
    reason,
    priority,
  }
}

function connectPair(
  srcDev: Device,
  srcPair: [Port, Port],
  tgtDev: Device,
  tgtPair: [Port, Port],
  usedOutputs: Set<string>,
  usedInputs: Set<string>,
  suggestions: SuggestedConnection[],
  reason: string,
  priority: number,
) {
  suggestions.push(
    makeSuggestion(srcDev, srcPair[0], tgtDev, tgtPair[0], reason, priority),
  )
  suggestions.push(
    makeSuggestion(srcDev, srcPair[1], tgtDev, tgtPair[1], reason, priority),
  )
  markUsed(usedOutputs, usedInputs, srcDev, srcPair[0], tgtDev, tgtPair[0])
  markUsed(usedOutputs, usedInputs, srcDev, srcPair[1], tgtDev, tgtPair[1])
}

// ── Main engine ──

export function generateSuggestions(
  devices: Device[],
  existingConnections: Connection[],
): SuggestedConnection[] {
  if (devices.length < 2) return []

  // Track globally used ports
  const usedOutputs = new Set<string>()
  const usedInputs = new Set<string>()

  for (const conn of existingConnections) {
    usedOutputs.add(`${conn.sourceDeviceId}:${conn.sourcePortId}`)
    usedInputs.add(`${conn.targetDeviceId}:${conn.targetPortId}`)

    // Mark exclusive-group siblings of already-connected ports
    const srcDev = devices.find(d => d.id === conn.sourceDeviceId)
    if (srcDev) {
      const srcPort = srcDev.outputs.find(p => p.id === conn.sourcePortId)
      if (srcPort?.exclusiveGroup) {
        for (const sib of srcDev.outputs) {
          if (
            sib.id !== srcPort.id &&
            sib.exclusiveGroup === srcPort.exclusiveGroup
          ) {
            usedOutputs.add(`${srcDev.id}:${sib.id}`)
          }
        }
      }
    }
    const tgtDev = devices.find(d => d.id === conn.targetDeviceId)
    if (tgtDev) {
      const tgtPort = tgtDev.inputs.find(p => p.id === conn.targetPortId)
      if (tgtPort?.exclusiveGroup) {
        for (const sib of tgtDev.inputs) {
          if (
            sib.id !== tgtPort.id &&
            sib.exclusiveGroup === tgtPort.exclusiveGroup
          ) {
            usedInputs.add(`${tgtDev.id}:${sib.id}`)
          }
        }
      }
    }
  }

  const suggestions: SuggestedConnection[] = []

  // Classify devices
  const interfaces = devices.filter(d => d.category === 'interface')
  const mixers = devices.filter(d => d.category === 'mixer')
  const sources = devices.filter(d => AUDIO_SOURCES.includes(d.category))
  const samplers = devices.filter(d => d.category === 'sampler')
  const effects = devices.filter(d => d.category === 'effects')
  const monitors = devices.filter(
    d =>
      d.category === 'monitoring' &&
      !d.name.toLowerCase().includes('sub') &&
      !d.name.toLowerCase().includes('controller'),
  )
  const subs = devices.filter(
    d => d.category === 'monitoring' && d.name.toLowerCase().includes('sub'),
  )
  const monitorControllers = devices.filter(
    d =>
      d.category === 'monitoring' &&
      d.name.toLowerCase().includes('controller'),
  )
  const controllers = devices.filter(d => d.category === 'controller')
  const midiInterfaces = devices.filter(d => d.category === 'midi_interface')

  const hasMixer = mixers.length > 0
  const hasMidiInterface = midiInterfaces.length > 0

  // ════════════════════════════════════════════════════════════
  // 1. MONITORING CHAIN (highest priority — establishes the
  //    output path before we route sources into it)
  // ════════════════════════════════════════════════════════════

  // Determine the primary interface and primary mixer
  const primaryInterface = interfaces[0] || null
  const primaryMixer = mixers[0] || null
  const primaryMonCtrl = monitorControllers[0] || null
  const primarySub = subs[0] || null

  // Build monitoring chain: Source → Monitor Controller → Sub → Monitors
  // The "source" of the monitoring chain is the mixer (if present) or interface

  const monitorChainSource = primaryMixer || primaryInterface

  if (monitorChainSource) {
    // Step 1: MonitorChainSource → Monitor Controller (if present)
    if (primaryMonCtrl) {
      const srcPair = getFirstAvailablePair(
        monitorChainSource.outputs,
        usedOutputs,
        monitorChainSource.id,
        hasMixer ? isMainOutput : isLineOut,
      )
      const tgtPair = getFirstAvailablePair(
        primaryMonCtrl.inputs,
        usedInputs,
        primaryMonCtrl.id,
      )

      if (srcPair && tgtPair) {
        connectPair(
          monitorChainSource,
          srcPair,
          primaryMonCtrl,
          tgtPair,
          usedOutputs,
          usedInputs,
          suggestions,
          `${hasMixer ? 'Mixer' : 'Interface'} → Monitor Controller`,
          100,
        )
      }

      // Step 2: Monitor Controller → Sub (if present)
      if (primarySub) {
        const mcOutPair = getFirstAvailablePair(
          primaryMonCtrl.outputs,
          usedOutputs,
          primaryMonCtrl.id,
        )
        const subInPair = getFirstAvailablePair(
          primarySub.inputs,
          usedInputs,
          primarySub.id,
        )

        if (mcOutPair && subInPair) {
          connectPair(
            primaryMonCtrl,
            mcOutPair,
            primarySub,
            subInPair,
            usedOutputs,
            usedInputs,
            suggestions,
            'Monitor Controller → Subwoofer',
            99,
          )
        }

        // Step 3: Sub thru → Monitors
        for (const monitor of monitors) {
          const connected = connectToMonitor(
            primarySub,
            primarySub.outputs,
            monitor,
            usedOutputs,
            usedInputs,
            suggestions,
            'Subwoofer thru → Monitor',
            98,
            isThruOutput,
          )
          if (!connected) {
            // If sub doesn't have thru outs left, try monitor controller
            connectToMonitor(
              primaryMonCtrl,
              primaryMonCtrl.outputs,
              monitor,
              usedOutputs,
              usedInputs,
              suggestions,
              'Monitor Controller → Monitor',
              97,
            )
          }
        }
      } else {
        // No sub: Monitor Controller → Monitors directly
        for (const monitor of monitors) {
          connectToMonitor(
            primaryMonCtrl,
            primaryMonCtrl.outputs,
            monitor,
            usedOutputs,
            usedInputs,
            suggestions,
            'Monitor Controller → Monitor',
            98,
          )
        }
      }
    } else if (primarySub) {
      // No monitor controller, but has sub: Source → Sub → Monitors
      const srcPair = getFirstAvailablePair(
        monitorChainSource.outputs,
        usedOutputs,
        monitorChainSource.id,
        hasMixer ? isMainOutput : isLineOut,
      )
      const subInPair = getFirstAvailablePair(
        primarySub.inputs,
        usedInputs,
        primarySub.id,
      )

      if (srcPair && subInPair) {
        connectPair(
          monitorChainSource,
          srcPair,
          primarySub,
          subInPair,
          usedOutputs,
          usedInputs,
          suggestions,
          `${hasMixer ? 'Mixer' : 'Interface'} → Subwoofer`,
          100,
        )
      }

      for (const monitor of monitors) {
        connectToMonitor(
          primarySub,
          primarySub.outputs,
          monitor,
          usedOutputs,
          usedInputs,
          suggestions,
          'Subwoofer thru → Monitor',
          99,
          isThruOutput,
        )
      }
    } else {
      // No monitor controller, no sub: Source → Monitors directly
      for (const monitor of monitors) {
        connectToMonitor(
          monitorChainSource,
          monitorChainSource.outputs,
          monitor,
          usedOutputs,
          usedInputs,
          suggestions,
          `${hasMixer ? 'Mixer' : 'Interface'} → Monitor`,
          100,
          hasMixer ? isMainOutput : isLineOut,
        )
      }
    }
  }

  // ════════════════════════════════════════════════════════════
  // 2. MIXER → INTERFACE (main outs only)
  // ════════════════════════════════════════════════════════════

  if (primaryMixer && primaryInterface) {
    const mixMainPair = getFirstAvailablePair(
      primaryMixer.outputs,
      usedOutputs,
      primaryMixer.id,
      isMainOutput,
    )
    const ifaceInPair = getFirstAvailablePair(
      primaryInterface.inputs,
      usedInputs,
      primaryInterface.id,
      isLineIn,
    )

    if (mixMainPair && ifaceInPair) {
      connectPair(
        primaryMixer,
        mixMainPair,
        primaryInterface,
        ifaceInPair,
        usedOutputs,
        usedInputs,
        suggestions,
        'Mixer main → Interface',
        95,
      )
    }
  }

  // ════════════════════════════════════════════════════════════
  // 3. AUDIO SOURCES → MIXER or INTERFACE
  // ════════════════════════════════════════════════════════════

  const audioTarget = primaryMixer || primaryInterface
  if (audioTarget) {
    for (const source of sources) {
      // Get a stereo pair of audio outputs from the source
      const srcPair = getFirstAvailablePair(
        source.outputs,
        usedOutputs,
        source.id,
        p => p.signalType === 'audio' && !isThruOutput(p),
      )

      if (!srcPair) continue

      // Find available input pair on the target
      const tgtPair = getFirstAvailablePair(
        audioTarget.inputs,
        usedInputs,
        audioTarget.id,
        hasMixer ? undefined : isLineIn,
      )

      if (tgtPair) {
        connectPair(
          source,
          srcPair,
          audioTarget,
          tgtPair,
          usedOutputs,
          usedInputs,
          suggestions,
          `Audio source → ${hasMixer ? 'Mixer' : 'Interface'}`,
          85,
        )
      }
    }
  }

  // ════════════════════════════════════════════════════════════
  // 4. EFFECTS → MIXER (or INTERFACE)
  // ════════════════════════════════════════════════════════════

  const fxTarget = primaryMixer || primaryInterface
  if (fxTarget) {
    for (const fx of effects) {
      const fxOutPair = getFirstAvailablePair(
        fx.outputs,
        usedOutputs,
        fx.id,
        p => p.signalType === 'audio',
      )
      const tgtPair = getFirstAvailablePair(
        fxTarget.inputs,
        usedInputs,
        fxTarget.id,
        hasMixer ? undefined : isLineIn,
      )

      if (fxOutPair && tgtPair) {
        connectPair(
          fx,
          fxOutPair,
          fxTarget,
          tgtPair,
          usedOutputs,
          usedInputs,
          suggestions,
          `Effects → ${hasMixer ? 'Mixer' : 'Interface'}`,
          80,
        )
      }
    }
  }

  // ════════════════════════════════════════════════════════════
  // 5. SAMPLER INPUTS (from interface outputs for resampling)
  // ════════════════════════════════════════════════════════════

  if (primaryInterface) {
    for (const sampler of samplers) {
      const samplerAudioIns = sampler.inputs.filter(
        p => p.signalType === 'audio',
      )
      if (samplerAudioIns.length === 0) continue

      const samplerInPair = getFirstAvailablePair(
        sampler.inputs,
        usedInputs,
        sampler.id,
        p => p.signalType === 'audio',
      )
      const ifaceOutPair = getFirstAvailablePair(
        primaryInterface.outputs,
        usedOutputs,
        primaryInterface.id,
        isLineOut,
      )

      if (samplerInPair && ifaceOutPair) {
        connectPair(
          primaryInterface,
          ifaceOutPair,
          sampler,
          samplerInPair,
          usedOutputs,
          usedInputs,
          suggestions,
          'Interface → Sampler (resample)',
          70,
        )
      }
    }
  }

  // ════════════════════════════════════════════════════════════
  // 6. MIDI ROUTING
  // ════════════════════════════════════════════════════════════

  const midiReceivers = devices.filter(d => AUDIO_SOURCES.includes(d.category))

  if (hasMidiInterface) {
    // Controller → MIDI Interface
    for (const ctrl of controllers) {
      const midiOut = ctrl.outputs.find(
        p => p.signalType === 'midi' && !usedOutputs.has(`${ctrl.id}:${p.id}`),
      )
      const midiIfaceIn = midiInterfaces[0].inputs.find(
        p =>
          p.signalType === 'midi' &&
          !usedInputs.has(`${midiInterfaces[0].id}:${p.id}`),
      )

      if (midiOut && midiIfaceIn) {
        suggestions.push(
          makeSuggestion(
            ctrl,
            midiOut,
            midiInterfaces[0],
            midiIfaceIn,
            'Controller → MIDI Interface',
            85,
          ),
        )
        markUsed(
          usedOutputs,
          usedInputs,
          ctrl,
          midiOut,
          midiInterfaces[0],
          midiIfaceIn,
        )
      }
    }

    // MIDI Interface → Sound sources
    for (const receiver of midiReceivers) {
      const midiIn = receiver.inputs.find(
        p =>
          p.signalType === 'midi' && !usedInputs.has(`${receiver.id}:${p.id}`),
      )
      const midiIfaceOut = midiInterfaces[0].outputs.find(
        p =>
          p.signalType === 'midi' &&
          !usedOutputs.has(`${midiInterfaces[0].id}:${p.id}`),
      )

      if (midiIn && midiIfaceOut) {
        suggestions.push(
          makeSuggestion(
            midiInterfaces[0],
            midiIfaceOut,
            receiver,
            midiIn,
            'MIDI Interface → Sound source',
            80,
          ),
        )
        markUsed(
          usedOutputs,
          usedInputs,
          midiInterfaces[0],
          midiIfaceOut,
          receiver,
          midiIn,
        )
      }
    }
  } else {
    // Direct: Controller → Sound sources
    for (const ctrl of controllers) {
      for (const receiver of midiReceivers) {
        const midiOut = ctrl.outputs.find(
          p =>
            p.signalType === 'midi' && !usedOutputs.has(`${ctrl.id}:${p.id}`),
        )
        const midiIn = receiver.inputs.find(
          p =>
            p.signalType === 'midi' &&
            !usedInputs.has(`${receiver.id}:${p.id}`),
        )

        if (midiOut && midiIn) {
          suggestions.push(
            makeSuggestion(
              ctrl,
              midiOut,
              receiver,
              midiIn,
              'Controller → Sound source (MIDI)',
              50,
            ),
          )
          markUsed(usedOutputs, usedInputs, ctrl, midiOut, receiver, midiIn)
        }
      }
    }
  }

  // ════════════════════════════════════════════════════════════
  // 7. ADAT (interface ↔ interface expansion)
  //    Handles both directions independently so input expanders
  //    (expander ADAT Out → primary ADAT In) and output expanders
  //    (primary ADAT Out → expander ADAT In) both get linked.
  // ════════════════════════════════════════════════════════════

  if (interfaces.length > 1 && primaryInterface) {
    for (let i = 1; i < interfaces.length; i++) {
      const expander = interfaces[i]

      // Direction A: expander → primary (input expander like AudioFuse X8 IN)
      const expanderAdatOut = expander.outputs.find(
        p =>
          p.signalType === 'adat' && !usedOutputs.has(`${expander.id}:${p.id}`),
      )
      const primaryAdatIn = primaryInterface.inputs.find(
        p =>
          p.signalType === 'adat' &&
          !usedInputs.has(`${primaryInterface.id}:${p.id}`),
      )
      if (expanderAdatOut && primaryAdatIn) {
        suggestions.push(
          makeSuggestion(
            expander,
            expanderAdatOut,
            primaryInterface,
            primaryAdatIn,
            'ADAT input expansion',
            75,
          ),
        )
        markUsed(
          usedOutputs,
          usedInputs,
          expander,
          expanderAdatOut,
          primaryInterface,
          primaryAdatIn,
        )
      }

      // Direction B: primary → expander (output expander like AudioFuse X8 OUT)
      const primaryAdatOut = primaryInterface.outputs.find(
        p =>
          p.signalType === 'adat' &&
          !usedOutputs.has(`${primaryInterface.id}:${p.id}`),
      )
      const expanderAdatIn = expander.inputs.find(
        p =>
          p.signalType === 'adat' && !usedInputs.has(`${expander.id}:${p.id}`),
      )
      if (primaryAdatOut && expanderAdatIn) {
        suggestions.push(
          makeSuggestion(
            primaryInterface,
            primaryAdatOut,
            expander,
            expanderAdatIn,
            'ADAT output expansion',
            75,
          ),
        )
        markUsed(
          usedOutputs,
          usedInputs,
          primaryInterface,
          primaryAdatOut,
          expander,
          expanderAdatIn,
        )
      }
    }
  }

  // ════════════════════════════════════════════════════════════
  // 8. USB ROUTING (computer/usb_hub → devices with USB ports)
  // ════════════════════════════════════════════════════════════

  const usbHosts = devices.filter(d => USB_HOSTS.includes(d.category))
  if (usbHosts.length > 0) {
    const usbDevices = devices.filter(
      d =>
        !USB_HOSTS.includes(d.category) &&
        d.inputs.some(p => p.signalType === 'usb'),
    )

    for (const usbDev of usbDevices) {
      const usbIn = usbDev.inputs.find(
        p => p.signalType === 'usb' && !usedInputs.has(`${usbDev.id}:${p.id}`),
      )
      if (!usbIn) continue

      // Find a USB host with an available USB output
      for (const host of usbHosts) {
        const usbOut = host.outputs.find(
          p => p.signalType === 'usb' && !usedOutputs.has(`${host.id}:${p.id}`),
        )
        if (usbOut) {
          suggestions.push(
            makeSuggestion(host, usbOut, usbDev, usbIn, 'USB connection', 60),
          )
          markUsed(usedOutputs, usedInputs, host, usbOut, usbDev, usbIn)
          break
        }
      }
    }
  }

  // ════════════════════════════════════════════════════════════
  // 9. ETHERNET (network_switch → devices with ethernet ports)
  // ════════════════════════════════════════════════════════════

  const networkSwitches = devices.filter(d => d.category === 'network_switch')
  if (networkSwitches.length > 0) {
    const ethernetDevices = devices.filter(
      d =>
        d.category !== 'network_switch' &&
        d.inputs.some(p => p.signalType === 'ethernet'),
    )

    for (const ethDev of ethernetDevices) {
      const ethIn = ethDev.inputs.find(
        p =>
          p.signalType === 'ethernet' &&
          !usedInputs.has(`${ethDev.id}:${p.id}`),
      )
      if (!ethIn) continue

      for (const sw of networkSwitches) {
        const ethOut = sw.outputs.find(
          p =>
            p.signalType === 'ethernet' && !usedOutputs.has(`${sw.id}:${p.id}`),
        )
        if (ethOut) {
          suggestions.push(
            makeSuggestion(sw, ethOut, ethDev, ethIn, 'Ethernet link', 55),
          )
          markUsed(usedOutputs, usedInputs, sw, ethOut, ethDev, ethIn)
          break
        }
      }
    }
  }

  // Sort by priority descending
  return suggestions.sort((a, b) => b.priority - a.priority)
}
